import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWebRTC } from '../context/WebRTCContext';
import { useFeature } from '../hooks/useFeatures';
import './GazeOverlay.css';

// Fading-heatmap tuning. Points older than DECAY_MS have fully faded out; the
// buffer is capped so a long session can't grow it without bound.
const HEATMAP_DECAY_MS = 6000;
const HEATMAP_MAX_POINTS = 800;

const nowMs = () =>
    (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());

/**
 * useGazeTracking - Hook for gaze tracking state management
 *
 * Uses requestAnimationFrame interpolation for smooth rendering.
 * Gaze data updates are stored in refs (not state) to avoid React
 * re-renders on every frame. Only availability + the selected mode
 * ('off' | 'tracker' | 'heatmap') trigger re-renders.
 */
export const useGazeTracking = (videoElement) => {
    const { isConnected, connectedPeers } = useWebRTC();
    // useFeature polls until registration completes — replaces a bare
    // featureManager.getFeature call that could return null on cold load.
    const gazeFeature = useFeature('gaze-tracking');
    const peerCount = connectedPeers?.length ?? 0;

    const [isAvailable, setIsAvailable] = useState(false);
    // 'off' — no overlay; 'tracker' — live reticle; 'heatmap' — fading heatmap.
    const [mode, setMode] = useState('off');
    const isEnabled = mode !== 'off';

    // Gaze position stored in refs — no React re-renders for movement
    const targetRef = useRef({ x: 0, y: 0 }); // Latest position from data
    const currentRef = useRef({ x: 0, y: 0 }); // Interpolated display position
    const isValidRef = useRef(false);
    const hasDataRef = useRef(false);
    const rafIdRef = useRef(null);
    const trackerElRef = useRef(null); // DOM ref set by GazeOverlay
    const unsubscribeRef = useRef(null);
    // Normalized {nx, ny, t} points feeding the fading heatmap (bounded buffer).
    const heatmapPointsRef = useRef([]);
    // Tracks whether THIS hook instance started the desktop stream. Lets us
    // avoid stopping a stream another consumer started (e.g. the
    // /app/integrations/gaze-tracking page) when this overlay mounts/unmounts
    // with mode='off'.
    const weStartedStreamRef = useRef(false);

    // Availability — resilient to the cold-start race. The desktop answers
    // `get_status` over the peer DataChannel; if we ask before a peer is
    // reachable the request times out, and a one-shot check would then hide the
    // gaze toggle FOREVER. So: wait for a peer, retry on failure with backoff,
    // re-check on a slow cadence (a tracker/Tobii worker can come online later),
    // and honor any live status the feature pushes.
    useEffect(() => {
        if (!isConnected || !gazeFeature || peerCount === 0) {
            setIsAvailable(false);
            return;
        }

        let cancelled = false;
        let timer = null;
        let attempts = 0;

        const poll = async () => {
            let ok = false;
            try {
                const status = await gazeFeature.getStatus();
                if (cancelled) return;
                if (status?.success) {
                    ok = true;
                    attempts = 0;
                    setIsAvailable(!!status.available);
                }
            } catch { /* request failed — fall through to retry */ }
            if (cancelled) return;
            // On success re-check slowly; on failure retry quickly (capped) so a
            // transient cold-start timeout recovers instead of hiding the toggle.
            const delay = ok ? 15000 : Math.min(1000 * ++attempts, 5000);
            timer = setTimeout(poll, delay);
        };
        poll();

        const unsubStatus = gazeFeature.subscribeToStatus?.((s) => {
            if (!cancelled && s) setIsAvailable(!!s.available);
        });

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
            if (unsubStatus) unsubStatus();
        };
    }, [isConnected, gazeFeature, peerCount]);

    // Handle incoming gaze data — write to refs, no setState
    const handleGazeData = useCallback((data) => {
        if (!videoElement || !data.valid) {
            isValidRef.current = false;
            return;
        }

        const videoRect = videoElement.getBoundingClientRect();
        targetRef.current.x = data.normalizedX * videoRect.width;
        targetRef.current.y = data.normalizedY * videoRect.height;
        isValidRef.current = true;
        hasDataRef.current = true;

        // Accumulate a normalized point for the fading heatmap. Normalized (not
        // pixel) coords keep it correct across video resizes — the heatmap
        // canvas maps them to its own dimensions each frame.
        const pts = heatmapPointsRef.current;
        pts.push({ nx: data.normalizedX, ny: data.normalizedY, t: nowMs() });
        if (pts.length > HEATMAP_MAX_POINTS) pts.shift();
    }, [videoElement]);

    // rAF interpolation loop for the live reticle — only runs in 'tracker' mode.
    useEffect(() => {
        if (mode !== 'tracker') return;

        const LERP_FACTOR = 0.25; // 0 = frozen, 1 = instant snap. 0.25 = smooth pursuit
        const animate = () => {
            rafIdRef.current = requestAnimationFrame(animate);

            const el = trackerElRef.current;
            if (!el) return;

            if (!isValidRef.current || !hasDataRef.current) {
                el.style.opacity = '0';
                return;
            }

            // Adaptive lerp: faster when far away, slower when close
            const dx = targetRef.current.x - currentRef.current.x;
            const dy = targetRef.current.y - currentRef.current.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            // Use higher lerp for large jumps (snap), lower for small movements (smooth)
            const adaptiveLerp = dist > 100 ? 0.6 : LERP_FACTOR;

            currentRef.current.x += dx * adaptiveLerp;
            currentRef.current.y += dy * adaptiveLerp;

            el.style.opacity = '1';
            el.style.transform = `translate3d(${currentRef.current.x}px, ${currentRef.current.y}px, 0) translate(-50%, -50%)`;
        };

        rafIdRef.current = requestAnimationFrame(animate);

        return () => {
            if (rafIdRef.current) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };
    }, [mode]);

    // Subscribe/unsubscribe to gaze data — active whenever a mode is selected
    // (both the reticle and the heatmap consume the same stream).
    useEffect(() => {
        if (!gazeFeature) return;

        if (isEnabled) {
            unsubscribeRef.current = gazeFeature.subscribeToGaze(handleGazeData);
            gazeFeature.startStreaming();
            weStartedStreamRef.current = true;
        } else {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
            if (weStartedStreamRef.current) {
                gazeFeature.stopStreaming();
                weStartedStreamRef.current = false;
            }
            hasDataRef.current = false;
            isValidRef.current = false;
            heatmapPointsRef.current = [];
        }

        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, [isEnabled, gazeFeature, handleGazeData]);

    // Cleanup on unmount — stop streaming only if we started it. Uses the ref
    // (not isEnabled, which is stale because deps are empty) so this actually
    // fires when the consumer unmounts mid-stream.
    useEffect(() => {
        return () => {
            if (gazeFeature && weStartedStreamRef.current) {
                gazeFeature.stopStreaming();
                weStartedStreamRef.current = false;
            }
        };
    }, [gazeFeature]);

    return {
        isAvailable,
        mode,
        setMode,
        isEnabled,
        isValid: isValidRef,
        trackerElRef,
        heatmapPointsRef,
    };
};

/**
 * GazeOverlay - Displays the live eye-gaze reticle on the screen share video.
 *
 * Renders entirely via requestAnimationFrame + direct DOM transforms.
 * React only handles mount/unmount — position updates bypass React completely
 * for silky smooth interpolation between data points. Only shown in 'tracker'
 * mode; the heatmap mode is rendered by <GazeHeatmap>.
 */
const GazeOverlay = ({ gazeState }) => {
    if (!gazeState || gazeState.mode !== 'tracker') return null;

    const { trackerElRef } = gazeState;

    return (
        <div className="gaze-overlay">
            {/* Position is set entirely by the rAF loop in useGazeTracking via transform */}
            <div
                ref={trackerElRef}
                className="gaze-tracker"
                style={{ opacity: 0, left: 0, top: 0 }}
            >
                <div className="gaze-ring-whisper" />
                <div className="gaze-ring-outer" />
                <div className="gaze-ring" />
                <div className="gaze-bracket gaze-bracket-tl" />
                <div className="gaze-bracket gaze-bracket-tr" />
                <div className="gaze-bracket gaze-bracket-bl" />
                <div className="gaze-bracket gaze-bracket-br" />
                <div className="gaze-crosshair gaze-crosshair-h" />
                <div className="gaze-crosshair gaze-crosshair-v" />
                <div className="gaze-center" />
            </div>
        </div>
    );
};

/**
 * GazeHeatmap - Fading heatmap of where the subject has looked over time.
 *
 * Draws the buffered gaze points (from useGazeTracking) as additive amber
 * blooms whose alpha decays with age, plus a pulsing live dot at the current
 * point. Canvas-based and rAF-driven, like the eye-lock heatmap. Only shown in
 * 'heatmap' mode.
 */
export const GazeHeatmap = ({ gazeState }) => {
    const canvasRef = useRef(null);
    const pulseRef = useRef(0);
    const mode = gazeState?.mode;

    useEffect(() => {
        if (!gazeState || mode !== 'heatmap') return;
        const canvas = canvasRef.current;
        if (!canvas) return;

        const { heatmapPointsRef, isValid } = gazeState;
        let raf = null;

        const draw = () => {
            raf = requestAnimationFrame(draw);

            const w = canvas.clientWidth;
            const h = canvas.clientHeight;
            if (!w || !h) return;
            if (canvas.width !== w) canvas.width = w;
            if (canvas.height !== h) canvas.height = h;

            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            ctx.clearRect(0, 0, w, h);

            const now = nowMs();
            const pts = heatmapPointsRef.current;
            // Evict fully-faded points from the front (buffer is time-ordered).
            while (pts.length && now - pts[0].t > HEATMAP_DECAY_MS) pts.shift();

            // Additive blooms so overlapping gaze builds heat.
            ctx.globalCompositeOperation = 'lighter';
            for (const p of pts) {
                const life = 1 - (now - p.t) / HEATMAP_DECAY_MS;
                if (life <= 0) continue;
                const px = p.nx * w;
                const py = p.ny * h;
                const r = 28;
                const grad = ctx.createRadialGradient(px, py, 0, px, py, r);
                grad.addColorStop(0, `rgba(245, 158, 11, ${0.16 * life})`);
                grad.addColorStop(1, 'rgba(245, 158, 11, 0)');
                ctx.fillStyle = grad;
                ctx.fillRect(px - r, py - r, r * 2, r * 2);
            }
            ctx.globalCompositeOperation = 'source-over';

            // Pulsing live dot at the newest point.
            if (isValid.current && pts.length) {
                const p = pts[pts.length - 1];
                const px = p.nx * w;
                const py = p.ny * h;
                pulseRef.current += 0.08;
                const radius = 7 * (1 + 0.3 * Math.sin(pulseRef.current));
                ctx.beginPath();
                ctx.arc(px, py, radius, 0, Math.PI * 2);
                ctx.fillStyle = 'rgba(251, 191, 36, 0.9)';
                ctx.fill();
                ctx.beginPath();
                ctx.arc(px, py, radius + 5, 0, Math.PI * 2);
                ctx.strokeStyle = 'rgba(245, 158, 11, 0.4)';
                ctx.lineWidth = 2;
                ctx.stroke();
            }
        };

        raf = requestAnimationFrame(draw);
        return () => { if (raf) cancelAnimationFrame(raf); };
    }, [mode, gazeState]);

    if (!gazeState || mode !== 'heatmap') return null;

    return (
        <div className="gaze-overlay gaze-heatmap">
            <canvas ref={canvasRef} className="gaze-heatmap-canvas" />
        </div>
    );
};

export default GazeOverlay;
